import { Injectable, inject } from '@angular/core';
import { SwPush } from '@angular/service-worker';
import { Observable, BehaviorSubject } from 'rxjs';
import { CustomNotificationAction } from './pwa.service';

export interface NotificationPayload {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  image?: string;
  data?: any;
  actions?: CustomNotificationAction[];
  tag?: string;
  requireInteraction?: boolean;
  silent?: boolean;
  timestamp?: number;
}

@Injectable({
  providedIn: 'root'
})
export class PushNotificationService {
  private swPush = inject(SwPush);

  private readonly VAPID_PUBLIC_KEY = 'YOUR_VAPID_PUBLIC_KEY'; // Replace with actual key
  private permissionSubject = new BehaviorSubject<NotificationPermission>('default');

  public permission$ = this.permissionSubject.asObservable();

  constructor() {
    this.initializePermission();
    this.setupPushSubscription();
  }

  /**
   * Initialize notification permission status
   */
  private initializePermission(): void {
    if ('Notification' in window) {
      this.permissionSubject.next(Notification.permission);
    }
  }

  /**
   * Setup push subscription handling
   */
  private setupPushSubscription(): void {
    if (!this.swPush.isEnabled) {
      console.log('Push notifications are not enabled');
      return;
    }

    // Handle push messages
    this.swPush.messages.subscribe((message: any) => {
      console.log('Push message received:', message);
      this.showNotification(message);
    });

    // Handle notification clicks
    this.swPush.notificationClicks.subscribe((click: any) => {
      console.log('Notification clicked:', click);
      this.handleNotificationClick(click);
    });
  }

  /**
   * Request notification permission
   */
  async requestPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      console.log('This browser does not support notifications');
      return 'denied';
    }

    const permission = await Notification.requestPermission();
    this.permissionSubject.next(permission);
    return permission;
  }

  /**
   * Check if notifications are supported
   */
  isSupported(): boolean {
    return 'Notification' in window && this.swPush.isEnabled;
  }

  /**
   * Check if notifications are enabled
   */
  isEnabled(): boolean {
    return this.permissionSubject.value === 'granted';
  }

  /**
   * Subscribe to push notifications
   */
  async subscribeToPush(): Promise<PushSubscription | null> {
    if (!this.swPush.isEnabled) {
      console.log('Push notifications are not enabled');
      return null;
    }

    try {
      const subscription = await this.swPush.requestSubscription({
        serverPublicKey: this.VAPID_PUBLIC_KEY
      });

      console.log('Push subscription successful:', subscription);
      return subscription;
    } catch (error) {
      console.error('Could not subscribe to push notifications:', error);
      return null;
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribeFromPush(): Promise<boolean> {
    if (!this.swPush.isEnabled) {
      return false;
    }

    try {
      await this.swPush.unsubscribe();
      console.log('Push unsubscription successful');
      return true;
    } catch (error) {
      console.error('Could not unsubscribe from push notifications:', error);
      return false;
    }
  }

  /**
   * Show local notification
   */
  async showNotification(payload: NotificationPayload): Promise<void> {
    if (!this.isEnabled()) {
      console.log('Notifications are not enabled');
      return;
    }

    const options: NotificationOptions = {
      body: payload.body,
      icon: payload.icon || '/assets/icons/icon-192x192.png',
      badge: payload.badge || '/assets/icons/badge-72x72.png',
      data: payload.data,
      tag: payload.tag,
      requireInteraction: payload.requireInteraction || false,
      silent: payload.silent || false
    };

    // Add image and actions if supported
    if (payload.image && 'image' in Notification.prototype) {
      (options as any).image = payload.image;
    }

    if (payload.actions && 'actions' in Notification.prototype) {
      (options as any).actions = payload.actions;
    }

    try {
      if ('serviceWorker' in navigator && 'showNotification' in ServiceWorkerRegistration.prototype) {
        // Use Service Worker to show notification
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          await registration.showNotification(payload.title, options);
        }
      } else {
        // Fallback to regular notification
        new Notification(payload.title, options);
      }
    } catch (error) {
      console.error('Error showing notification:', error);
    }
  }

  /**
   * Handle notification click events
   */
  private handleNotificationClick(click: any): void {
    const { action, notification } = click;

    // Close the notification
    notification.close();

    // Handle different actions
    switch (action) {
      case 'open':
        // Open the app
        this.openApp(notification.data?.url);
        break;
      case 'dismiss':
        // Just close the notification
        break;
      default:
        // Default action (notification body click)
        this.openApp(notification.data?.url);
        break;
    }
  }

  /**
   * Open the app or specific URL
   */
  private openApp(url?: string): void {
    const targetUrl = url || '/';

    if ('clients' in self) {
      // Service Worker context
      (self as any).clients.openWindow(targetUrl);
    } else {
      // Main thread context
      window.open(targetUrl, '_blank');
    }
  }

  /**
   * Schedule a local notification
   */
  async scheduleNotification(payload: NotificationPayload, delay: number): Promise<void> {
    setTimeout(() => {
      this.showNotification(payload);
    }, delay);
  }

  /**
   * Get current push subscription
   */
  getCurrentSubscription(): Observable<PushSubscription | null> {
    return this.swPush.subscription;
  }

  /**
   * Send subscription to server
   */
  async sendSubscriptionToServer(subscription: PushSubscription, userId?: string): Promise<boolean> {
    try {
      const response = await fetch('/api/push/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subscription,
          userId
        })
      });

      return response.ok;
    } catch (error) {
      console.error('Error sending subscription to server:', error);
      return false;
    }
  }

  /**
   * Remove subscription from server
   */
  async removeSubscriptionFromServer(subscription: PushSubscription, userId?: string): Promise<boolean> {
    try {
      const response = await fetch('/api/push/unsubscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subscription,
          userId
        })
      });

      return response.ok;
    } catch (error) {
      console.error('Error removing subscription from server:', error);
      return false;
    }
  }
}
